import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { fetchProductList } from '../services/product-list.service';
import type { ProductListParams } from '../types/product-list.types';

/**
 * Paginated product list for step 2 of the create-experiment wizard. Pass
 * `null` to keep the query disabled (e.g. before the user's `connectionId`
 * resolves). Query key includes the full params object so pagination /
 * search / sort changes refetch rather than serving a cached page.
 *
 * A `File` in the params can't go in the key — it serializes to `{}`, so two
 * different uploads would share one cache entry and the table would show the
 * first file's results for the second. It's replaced by a fingerprint that
 * changes whenever the file does.
 */
export function useProductList(params: ProductListParams | null) {
  const keyParams: Record<string, unknown> | null =
    params === null
      ? null
      : {
          ...(params as unknown as Record<string, unknown>),
          idsFile: params.idsFile
            ? `${params.idsFile.name}:${String(params.idsFile.size)}:${String(params.idsFile.lastModified)}`
            : undefined,
        };

  return useQuery({
    queryKey: queryKeys.products.list(params?.connectionId ?? '', keyParams ?? {}),
    queryFn: () => {
      if (!params) throw new Error('params is required');
      return fetchProductList(params);
    },
    enabled: params !== null,
    // Keep the current page on screen while the next one loads, instead of an empty table.
    placeholderData: keepPreviousData,
  });
}
